/**
 * F3 (assistant files): the files the assistant produced (`outputs/` of the chat session, written by its `create_file`
 * tool) are shown as download chips under its reply, apart from the user's own uploads (`inputs/`).
 * Pure helpers so the file-kind split, the chip data, the change detection and the download error texts can be unit
 * tested without the component. The download itself is `AssistantService.downloadFile` (an authenticated blob request;
 * no token ever goes into a URL) followed by `saveBlob`.
 */

/** A file the assistant produced, as kept on its reply (persisted in the transcript). */
export interface OutputFileChip {
    /** session-relative path, e.g. `outputs/orders.xlsx` (what the download endpoint takes) */
    path: string;
    /** file name shown on the chip and used for the saved file */
    name: string;
    bytes?: number;
}

export const OUTPUT_FOLDER = 'outputs/';

function pathOf(file: any): string {
    const raw = file && typeof file === 'object' ? file.path || file.name || '' : file || '';
    return String(raw).replace(/\\/g, '/');
}

/** Last path segment (the file name). */
export function fileNameOf(file: any): string {
    const path = pathOf(file);
    return path.includes('/') ? path.substring(path.lastIndexOf('/') + 1) : path;
}

/** A file the assistant produced: a real file inside the session's `outputs/` folder. */
export function isOutputFile(file: any): boolean {
    const path = pathOf(file);
    return path.startsWith(OUTPUT_FOLDER) && path.length > OUTPUT_FOLDER.length && !path.includes('..') && !path.endsWith('/');
}

/** The user's own uploads: everything in the list that is not an output (the caller drops screenshots / transcript first). */
export function isInputFile(file: any): boolean {
    return !isOutputFile(file);
}

/** Splits a session file list into the user's uploads and the assistant's outputs; a non-array gives two empty lists. */
export function splitFiles(files: any[] | null | undefined): {inputs: any[]; outputs: any[]} {
    const list = Array.isArray(files) ? files : [];
    return {inputs: list.filter(isInputFile), outputs: list.filter(isOutputFile)};
}

/** Size of a file as "812 B", "4.2 KB", "1.3 MB"; '' when unknown. */
export function fileSizeLabel(bytes: any): string {
    const n = Number(bytes);
    if (bytes === null || bytes === undefined || bytes === '' || !isFinite(n) || n < 0) {
        return '';
    }
    if (n < 1024) {
        return `${Math.round(n)} B`;
    }
    if (n < 1024 * 1024) {
        return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
    }
    return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** The chip for one listed output file. */
export function toOutputChip(file: any): OutputFileChip {
    const bytes = Number(file?.bytes ?? file?.size);
    const chip: OutputFileChip = {path: pathOf(file), name: fileNameOf(file)};
    if (isFinite(bytes) && bytes >= 0 && (file?.bytes !== undefined || file?.size !== undefined)) {
        chip.bytes = bytes;
    }
    return chip;
}

/** What identifies a version of a file: a rewrite of the same name changes its size or time. */
function versionOf(file: any): string {
    return `${file?.bytes ?? file?.size ?? ''}|${file?.modified_at ?? file?.modified ?? ''}`;
}

/** Remembers the outputs present before a turn (path -> version), to tell what the turn produced. */
export function outputBaseline(files: any[] | null | undefined): Map<string, string> {
    const map = new Map<string, string>();
    splitFiles(files).outputs.forEach((f) => map.set(pathOf(f), versionOf(f)));
    return map;
}

/** Outputs that are new or rewritten since `baseline`, as chips; with no baseline nothing can be told, so none. */
export function producedSince(baseline: Map<string, string> | null | undefined, files: any[] | null | undefined): OutputFileChip[] {
    if (!baseline) {
        return [];
    }
    return splitFiles(files)
        .outputs.filter((f) => baseline.get(pathOf(f)) !== versionOf(f))
        .map(toOutputChip);
}

/** Output files not shown on any reply yet (older chats, a turn whose baseline was lost): listed once under the last reply. */
export function unclaimedOutputs(
    files: any[] | null | undefined,
    messages: Array<{outputFiles?: OutputFileChip[]}> | null | undefined
): OutputFileChip[] {
    const claimed = new Set<string>();
    (messages || []).forEach((m) => (m?.outputFiles || []).forEach((c) => claimed.add(c.path)));
    return splitFiles(files)
        .outputs.filter((f) => !claimed.has(pathOf(f)))
        .map(toOutputChip);
}

/** A short chat message for a failed download: 404 (gone), 403 (not yours), anything else. */
export function downloadErrorText(error: any): string {
    const status = error?.status;
    if (status === 404 || status === 410) {
        return 'That file is no longer available.';
    }
    if (status === 403 || status === 401) {
        return "You can't download that file.";
    }
    if (status === 0 || status === 502 || status === 503 || status === 504) {
        return "Couldn't reach the server to download the file. Try again.";
    }
    return "Couldn't download the file. Try again.";
}

/**
 * Hands a downloaded blob to the browser as a file named `fileName` (object URL + anchor click, URL revoked afterwards).
 * `doc` is injectable for tests.
 */
export function saveBlob(
    blob: Blob,
    fileName: string,
    doc: Document = document,
    urlApi: Pick<typeof URL, 'createObjectURL' | 'revokeObjectURL'> = URL
): void {
    const url = urlApi.createObjectURL(blob);
    try {
        const a = doc.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener';
        a.style.display = 'none';
        doc.body.appendChild(a);
        a.click();
        doc.body.removeChild(a);
    } finally {
        // the click starts the save synchronously; revoke a little later so every browser has read the blob
        setTimeout(() => urlApi.revokeObjectURL(url), 10000);
    }
}
